import React, { useEffect, useState } from 'react'
import { Alert, Button, Card, Col, Form, InputGroup, Row } from 'react-bootstrap'
import { IoColorPaletteOutline } from 'react-icons/io5'
import ButtonSaveContent from '../../components/ButtonSaveContent'
import { SweetAlert } from '../../components/sweetalert'
import { DEFAULT_BRAND_COLOR, useTheme } from '../../context/theme'
import Appearance from '../../services/Appearance'
import { contrastOnWhite, normalizeHex } from '../../utilities/brandColors'
import MenuPreview from './MenuPreview'
import './style.css'

// Texto branco do menu legível (WCAG AA), a mesma regra do submenu no brandColors.js
const MIN_MENU_CONTRAST = 4.5

const withoutHash = (color) => color.replace(/^#/, '')

// Configurações > Menu: a cor do menu lateral e do topo, salva no banco e igual para todos os usuários. A prévia mostra a cor
// escolhida nos temas claro e escuro; o portal só muda ao salvar
const MenuSettings = () => {
  const { brandColor, setBrandColor } = useTheme()

  const [savedColor, setSavedColor] = useState(brandColor)
  const [draft, setDraft] = useState(withoutHash(brandColor)) // o que está no campo, sem o #
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const draftColor = normalizeHex(draft)
  const previewColor = draftColor || savedColor
  const contrast = contrastOnWhite(previewColor)
  const changed = Boolean(draftColor) && (draftColor !== savedColor)

  useEffect(() => {
    Appearance.find()
      .then(({ data }) => {
        const color = normalizeHex(data.menu_color) || DEFAULT_BRAND_COLOR
        setSavedColor(color)
        setDraft(withoutHash(color))
      })
      .catch((error) => {
        SweetAlert.error(error?.response?.data?.message || 'Não foi possível carregar a cor do menu.', 'Menu')
      })
      .finally(() => setLoading(false))
  }, [])

  function handleTextChange(event) {
    setDraft(event.target.value.replace(/[#\s]/g, '').slice(0, 6).toLowerCase())
  }

  function handleRestore() {
    setDraft(withoutHash(DEFAULT_BRAND_COLOR))
  }

  async function handleSubmit(event) {
    event.preventDefault()
    if (!draftColor) return SweetAlert.error('Informe a cor com 6 dígitos, como 680e8f.', 'Menu')

    setSaving(true)
    try {
      const { data } = await Appearance.update({ menu_color: draftColor })
      const color = normalizeHex(data.menu_color) || DEFAULT_BRAND_COLOR
      setSavedColor(color)
      setDraft(withoutHash(color))
      setBrandColor(color)
      SweetAlert.success('Cor do menu salva. Quem já está com o portal aberto vê a cor nova ao recarregar a página.', 'Menu')
    } catch (error) {
      SweetAlert.error(error?.response?.data?.message || 'Não foi possível salvar a cor do menu.', 'Menu')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='pb-4'>
      <div className='d-flex align-items-center gap-3 px-3 pt-3'>
        <div className='menu-settings-page-icon'><IoColorPaletteOutline /></div>
        <div className='d-flex flex-column textPrimary'>
          <h2 className='m-0'>Menu</h2>
          <small className='text-muted'>Cor do menu lateral e do topo, igual para todos os usuários</small>
        </div>
      </div>

      <Form className='px-3 pt-3' onSubmit={handleSubmit}>
        <Card className='cardShadow'>
          <Card.Body className='p-3 p-lg-4'>
            <Row className='g-4'>
              <Col xs={12} lg={5} className='d-flex flex-column gap-2'>
                <h3 className='menu-settings-section-title'>Cor do menu lateral</h3>
                <p className='small text-muted m-0'>
                  Vale nos temas claro e escuro. O submenu, os links e os gráficos do Dashboard saem desta cor.
                </p>

                <div className='d-flex align-items-start gap-2 mt-2'>
                  <Form.Control
                    type='color'
                    className='menu-settings-color'
                    value={previewColor}
                    onChange={(event) => setDraft(withoutHash(event.target.value))}
                    disabled={loading || saving}
                    aria-label='Escolher a cor do menu'
                    title='Escolher a cor do menu'
                  />
                  <InputGroup className='menu-settings-hex' hasValidation>
                    <InputGroup.Text>#</InputGroup.Text>
                    <Form.Control
                      value={draft}
                      onChange={handleTextChange}
                      maxLength={7}
                      placeholder={withoutHash(DEFAULT_BRAND_COLOR)}
                      isInvalid={!draftColor}
                      disabled={loading || saving}
                      aria-label='Cor do menu em hexadecimal'
                      spellCheck={false}
                    />
                    <Form.Control.Feedback type='invalid'>Use 6 dígitos (0 a 9 e a até f), como {withoutHash(DEFAULT_BRAND_COLOR)}.</Form.Control.Feedback>
                  </InputGroup>
                </div>

                <Button variant='link' size='sm' className='align-self-start px-0' onClick={handleRestore} disabled={loading || saving || (draftColor === DEFAULT_BRAND_COLOR)}>
                  Restaurar padrão ({DEFAULT_BRAND_COLOR})
                </Button>

                {(contrast < MIN_MENU_CONTRAST) ?
                  <Alert variant='warning' className='small m-0 py-2'>
                    Com esta cor, o texto branco do menu e os links do tema claro ficam difíceis de ler: contraste de
                    {' '}{contrast.toFixed(1).replace('.', ',')}:1, e o recomendado é pelo menos 4,5:1. Prefira um tom mais escuro.
                  </Alert> : ''}

                {changed ? <p className='small text-muted m-0'>A cor nova só vale depois de salvar.</p> : ''}
              </Col>

              <Col xs={12} lg={7} className='d-flex flex-column gap-2'>
                <h3 className='menu-settings-section-title'>Pré-visualização</h3>
                <p className='small text-muted m-0'>Como o portal fica com esta cor.</p>
                <div className='d-flex flex-column gap-3 mt-2'>
                  <MenuPreview color={previewColor} theme='light' label='Claro' />
                  <MenuPreview color={previewColor} theme='dark' label='Escuro' />
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <div className='d-flex justify-content-end mt-3'>
          <Button variant='success' type='submit' className='col-12 col-lg-auto d-flex align-items-center justify-content-center gap-1' disabled={loading || saving || !draftColor}>
            <ButtonSaveContent />
          </Button>
        </div>
      </Form>
    </div>
  )
}

export default MenuSettings
